import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { User } from '../_models/user.model';
import { Permission } from '../_models/permission.model';
import { Role } from '../_models/role.model';
import { catchError, map } from 'rxjs/operators';
import { QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import * as pako from 'pako';

const API_USERS_URL = 'api/users';
const API_PERMISSION_URL = 'api/permissions';
const API_ROLES_URL = 'api/roles';
const API_2FA = '/api/user/verifyotp';
const API_USER_URL = '/api/user';
const API_URL = environment.API_URL;

@Injectable()
export class AuthService {
    constructor(private readonly http: HttpClient) {}
    // Authentication/Authorization
    login(email: string, password: string, remember: boolean, recaptcha: String): Observable<HttpResponse<User>> {
        return this.http.post<User>(API_URL + API_USER_URL + '/login', { email, password, remember, 'g-recaptcha-response': recaptcha}, { observe: 'response' });
    }

    IDPlogin(params): Observable<HttpResponse<User>> {
        return this.http.post<User>(API_URL + API_USER_URL + '/login', params, { observe: 'response' });
    }

    verify(email: string, otp: number, remember: boolean, recaptcha: String): Observable<HttpResponse<User>> {
        // const userToken = localStorage.getItem(environment.runtime.authTokenKey);
        // const httpHeaders = new HttpHeaders({'Authorization': userToken});
        return this.http.post<User>(API_URL + API_2FA, { email, otp,remember, 'g-recaptcha-response': recaptcha }, { observe: 'response' });
    }

    logout(email: string) {
        const userToken = localStorage.getItem(environment.runtime.authTokenKey);
        const httpHeaders = new HttpHeaders({'Authorization': userToken});
        return this.http.post(API_URL + API_USER_URL + '/logout', {email}, { headers: httpHeaders })
    }

    getUserByToken(fields = ''): Observable<User> {
        const userToken = localStorage.getItem(environment.runtime.authTokenKey);
        const httpHeaders = new HttpHeaders({'Authorization': userToken});
        let params = new HttpParams();
        if (fields) {
            params = params.set('fields', fields);
        }
        return this.http.get<User>(API_URL + API_USER_URL, { headers: httpHeaders, params: params });
    }

    private get samlSsoUrl(): string {
        return environment.runtime.DIRECT_SAML_URL ||
            `https://login.microsoftonline.com/${environment.runtime.ssoClientId}/saml2`;
    }

    redirectToSAML() {
        if (environment.runtime.requireSigning) {
            const body = {
                spEntityId: `${location.protocol}//${location.hostname}`,
                spAcsUrl: `${location.protocol}//${location.hostname}/api/saml-login`,
                idpSsoUrl: this.samlSsoUrl
            };
            this.http.post<{ samlRequest: string }>(API_URL + '/api/auth/saml-login-request', body)
                .subscribe({
                    next: (res) => { window.location.href = `${this.samlSsoUrl}?${res.samlRequest}`; },
                    error: () => { window.location.href = `${this.samlSsoUrl}?SAMLRequest=${this.createSAMLRequest()}`; }
                });
        } else {
            window.location.href = `${this.samlSsoUrl}?SAMLRequest=${this.createSAMLRequest()}`;
        }
    }

    redirectToSAMLLogout(nameId: string) {
        const sessionIndex = localStorage.getItem('samlSessionIndex') || undefined;
        if (environment.runtime.requireSigning) {
            const body = {
                nameId,
                spEntityId: `${location.protocol}//${location.hostname}`,
                idpSloUrl: environment.runtime.SAML_LOGOUT_REDIRECT_URL,
                sessionIndex
            };
            this.http.post<{ samlRequest: string }>(API_URL + '/api/auth/saml-logout-request', body)
                .subscribe({
                    next: (res) => {
                        localStorage.removeItem('samlSessionIndex');
                        window.location.href = `${environment.runtime.SAML_LOGOUT_REDIRECT_URL}?${res.samlRequest}`;
                    },
                    error: () => {
                        const samlReq = this.createSAMLLogoutRequest(nameId, sessionIndex);
                        localStorage.removeItem('samlSessionIndex');
                        window.location.href = `${environment.runtime.SAML_LOGOUT_REDIRECT_URL}?SAMLRequest=${samlReq}`;
                    }
                });
        } else {
            const samlReq = this.createSAMLLogoutRequest(nameId, sessionIndex);
            localStorage.removeItem('samlSessionIndex');
            window.location.href = `${environment.runtime.SAML_LOGOUT_REDIRECT_URL}?SAMLRequest=${samlReq}`;
        }
    }

    createSAMLRequest() {
        let issueInstant = new Date().toISOString(),
			sAR = `<samlp:AuthnRequest xmlns="urn:oasis:names:tc:SAML:2.0:metadata" ID="id6c1c178c166d486687be4aaf5e482730" Version="2.0" IssueInstant="${issueInstant}" xmlns:samlp="urn:oasis:names:tc:SAML:2.0:protocol" AssertionConsumerServiceURL="${location.protocol}//${location.hostname}/api/saml-login"><Issuer xmlns="urn:oasis:names:tc:SAML:2.0:assertion">${location.protocol}//${location.hostname}</Issuer></samlp:AuthnRequest>`;

        let c = pako.deflateRaw(sAR, { to: 'string' } as any),
			b6E = encodeURIComponent(btoa(String.fromCodePoint.apply(null, c)));
        return b6E;
    }

    createSAMLLogoutRequest(userEmail: string, sessionIndex?: string) {
        // Generate unique ID for logout request
        let logoutRequestId = 'id' + this.generateRandomId(),
            issueInstant = new Date().toISOString(),
            issuer = `${location.protocol}//${location.hostname}`;

        const sessionIndexXml = sessionIndex ? `<samlp:SessionIndex>${sessionIndex}</samlp:SessionIndex>` : '';

        // Build SAML LogoutRequest XML
        let sLR = `<samlp:LogoutRequest xmlns:samlp="urn:oasis:names:tc:SAML:2.0:protocol" xmlns:saml="urn:oasis:names:tc:SAML:2.0:assertion" ID="${logoutRequestId}" Version="2.0" IssueInstant="${issueInstant}"><saml:Issuer>${issuer}</saml:Issuer><saml:NameID Format="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">${userEmail}</saml:NameID>${sessionIndexXml}</samlp:LogoutRequest>`;

        // Encode using deflate + base64 (same as login)
        let c = pako.deflateRaw(sLR, { to: 'string' } as any),
            b6E = encodeURIComponent(btoa(String.fromCodePoint.apply(null, c)));
        return b6E;
    }

    generateRandomId() {
        // Generate random ID without dashes (similar to backend UUID logic)
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    register(user: User): Observable<any> {
        let u = {
            organization: user.organization.trim(),
            name: user.name.trim(),
            email: user.email.trim(),
            password: user.password,
        }

        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<User>(API_URL + API_USER_URL + '/signUp', u, { headers: httpHeaders, observe: 'response' })
            .pipe(
                map((res: HttpResponse<User>) => {
                    return res;
                }),
                catchError(err => {
                    console.log(err)
                    return null;
                })
            );
    }

    resetPassword(user: User): Observable<any> {
        let u = {
            email: user.email.trim(),
            password: user.password,
            otp: user.otp,
            'g-recaptcha-response': user['g-recaptcha-response']
        }

        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<User>(API_URL + API_USER_URL + '/reset', u, { headers: httpHeaders, observe: 'response' })
            .pipe(
                map((res: HttpResponse<User>) => {
                    return res;
                }),
                catchError(err => {
                    console.log(err)
                    return null;
                })
            );
    }

    /*
     * Submit forgot password request
     *
     * @param {string} email
     * @returns {Observable<any>}
     */
    public requestPassword(email: string, recaptcha: String): Observable<any> {
    	return this.http.post(API_URL + API_USER_URL + '/forgot', {email, 'g-recaptcha-response': recaptcha})
    		.pipe(catchError(this.handleError('forgot-password', []))
	    );
    }


    getAllUsers(): Observable<User[]> {
		return this.http.get<User[]>(API_USERS_URL);
    }

    getUserById(userId: number): Observable<User> {
		return this.http.get<User>(API_USERS_URL + `/${userId}`);
	}


    // DELETE => delete the user from the server
	deleteUser(userId: number) {
		const url = `${API_USERS_URL}/${userId}`;
		return this.http.delete(url);
    }

    // UPDATE => PUT: update the user on the server
	updateUser(_user: User): Observable<any> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
		return this.http.put(API_USERS_URL, _user, { headers: httpHeaders });
	}

    // CREATE =>  POST: add a new user to the server
	createUser(user: User): Observable<User> {
    	const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
		return this.http.post<User>(API_USERS_URL, user, { headers: httpHeaders});
	}

    // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
	// items => filtered/sorted result
	findUsers(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
		return this.http.post<QueryResultsModel>(API_USERS_URL + '/findUsers', queryParams, { headers: httpHeaders});
    }

    // Permission
    getAllPermissions(): Observable<Permission[]> {
		return this.http.get<Permission[]>(API_PERMISSION_URL);
    }

    getRolePermissions(roleId: number): Observable<Permission[]> {
        return this.http.get<Permission[]>(API_PERMISSION_URL + '/getRolePermission?=' + roleId);
    }

    // Roles
    getAllRoles(): Observable<Role[]> {
        return this.http.get<Role[]>(API_ROLES_URL);
    }

    getRoleById(roleId: number): Observable<Role> {
		return this.http.get<Role>(API_ROLES_URL + `/${roleId}`);
    }

    // CREATE =>  POST: add a new role to the server
	createRole(role: Role): Observable<Role> {
		// Note: Add headers if needed (tokens/bearer)
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
		return this.http.post<Role>(API_ROLES_URL, role, { headers: httpHeaders});
	}

    // UPDATE => PUT: update the role on the server
	updateRole(role: Role): Observable<any> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
		return this.http.put(API_ROLES_URL, role, { headers: httpHeaders });
	}

	// DELETE => delete the role from the server
	deleteRole(roleId: number): Observable<Role> {
		const url = `${API_ROLES_URL}/${roleId}`;
		return this.http.delete<Role>(url);
	}

    // Check Role Before deletion
    isRoleAssignedToUsers(roleId: number): Observable<boolean> {
        return this.http.get<boolean>(API_ROLES_URL + '/checkIsRollAssignedToUser?roleId=' + roleId);
    }

    findRoles(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
        // This code imitates server calls
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
		return this.http.post<QueryResultsModel>(API_ROLES_URL + '/findRoles', queryParams, { headers: httpHeaders});
	}

 	/*
 	 * Handle Http operation that failed.
 	 * Let the app continue.
     *
	 * @param operation - name of the operation that failed
 	 * @param result - optional value to return as the observable result
 	 */
    private handleError<T>(operation = 'operation', result?: any) {
        return (error: any): Observable<any> => {
            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to console instead

            // Let the app keep running by returning an empty result.
            return of(result);
        };
    }
}
